package fr.frux.feature.map

import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxCard
import fr.frux.core.designsystem.format.formatDistance
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_empty_add_known
import fr.frux.feature.map.resources.map_empty_dismiss
import fr.frux.feature.map.resources.map_empty_nearest
import fr.frux.feature.map.resources.map_empty_seasonal_heading
import fr.frux.feature.map.resources.map_empty_show_nearest
import fr.frux.feature.map.resources.map_empty_subtitle
import fr.frux.feature.map.resources.map_empty_title
import fr.frux.feature.map.resources.map_empty_title_near
import fr.frux.feature.map.resources.map_empty_what_can_i_add
import fr.frux.shared.domain.model.SeasonalAround
import fr.frux.shared.domain.model.Species
import org.jetbrains.compose.resources.stringResource

/**
 * Zone vide sans filtre (#502, spec onboarding de contribution §2) : une invitation concrète
 * plutôt qu'un constat. Sans arbre à moins de [NEAR_TREE_THRESHOLD_KM], recenser est l'action
 * principale. Avec un arbre un peu plus loin, la découverte passe devant et l'invitation à
 * recenser descend en bouton secondaire. La caméra ne bouge que sur « Voir les arbres les plus
 * proches » (ADR 0009).
 *
 * @param nearestDistanceMeters Null tant que la requete du plus proche n'a pas repondu, ou
 *   quand rien n'existe en France : la ligne et le bouton sont alors absents, pas de promesse.
 */
@Composable
internal fun EmptyAreaCard(
    nearestDistanceMeters: Double?,
    seasonalAround: SeasonalAround,
    onShowNearest: () -> Unit,
    onSeasonSpeciesClick: (Species) -> Unit,
    onAddTree: () -> Unit,
    onWhatCanIAdd: () -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    val emphasis = emptyAreaEmphasis(nearestDistanceMeters)
    val titleRes =
        if (emphasis == EmptyAreaEmphasis.DISCOVER) Res.string.map_empty_title_near else Res.string.map_empty_title
    FruxCard(modifier = modifier.widthIn(max = MAX_CARD_WIDTH)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                text =
                    stringResource(titleRes),
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.weight(1f),
            )
            IconButton(onClick = onDismiss) {
                Icon(
                    imageVector = Icons.Outlined.Close,
                    contentDescription = stringResource(Res.string.map_empty_dismiss),
                )
            }
        }
        Text(
            text = stringResource(Res.string.map_empty_subtitle),
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
        )

        Spacer(modifier = Modifier.height(FruxSpacing.sm))
        when (emphasis) {
            EmptyAreaEmphasis.CONTRIBUTE -> {
                AddKnownTreeButton(onAddTree, FruxButtonStyle.Filled)
                if (nearestDistanceMeters != null) {
                    Spacer(modifier = Modifier.height(FruxSpacing.sm))
                    NearestTrees(nearestDistanceMeters, onShowNearest, FruxButtonStyle.Outline)
                }
            }

            EmptyAreaEmphasis.DISCOVER -> {
                if (nearestDistanceMeters != null) {
                    NearestTrees(nearestDistanceMeters, onShowNearest, FruxButtonStyle.Filled)
                }
                Spacer(modifier = Modifier.height(FruxSpacing.xs))
                AddKnownTreeButton(onAddTree, FruxButtonStyle.Outline)
            }
        }
        FruxButton(
            text = stringResource(Res.string.map_empty_what_can_i_add),
            onClick = onWhatCanIAdd,
            style = FruxButtonStyle.Text,
            modifier = Modifier.fillMaxWidth(),
        )

        if (seasonalAround.now.isNotEmpty() || seasonalAround.next.isNotEmpty()) {
            Spacer(modifier = Modifier.height(FruxSpacing.sm))
            SeasonalAroundSection(
                data = seasonalAround,
                onSpeciesClick = onSeasonSpeciesClick,
                showScope = false,
                heading = stringResource(Res.string.map_empty_seasonal_heading),
                modifier = Modifier.fillMaxWidth(),
            )
        }
    }
}

@Composable
private fun AddKnownTreeButton(
    onAddTree: () -> Unit,
    style: FruxButtonStyle,
) {
    FruxButton(
        text = stringResource(Res.string.map_empty_add_known),
        onClick = onAddTree,
        style = style,
        modifier = Modifier.fillMaxWidth(),
    )
}

@Composable
private fun NearestTrees(
    distanceMeters: Double,
    onShowNearest: () -> Unit,
    style: FruxButtonStyle,
) {
    Text(
        text = stringResource(Res.string.map_empty_nearest, formatDistance(distanceMeters)),
        style = MaterialTheme.typography.bodyMedium,
    )
    Spacer(modifier = Modifier.height(FruxSpacing.xs))
    FruxButton(
        text = stringResource(Res.string.map_empty_show_nearest),
        onClick = onShowNearest,
        style = style,
        modifier = Modifier.fillMaxWidth(),
    )
}

private val MAX_CARD_WIDTH = 480.dp
